import React, { useEffect, useRef, useState } from 'react';
import { Button, App, Image, Upload, Alert } from 'antd';
import { DeleteOutlined, EyeOutlined, InboxOutlined } from '@ant-design/icons';
import { trpc } from '../../lib/trpc';
import { withDevModalFilePath } from '../common';
const { Dragger } = Upload;

interface CompanyHeaderImageSettingsProps {
  companyId: string;
  isManager: boolean;
}

const { Modal } = withDevModalFilePath('client/src/components/company/CompanyHeaderImageSettings.tsx');

const CompanyHeaderImageSettings: React.FC<CompanyHeaderImageSettingsProps> = ({
  companyId,
  isManager,
}) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const utils = trpc.useUtils();
  const { message: messageApi } = App.useApp();
  const localPreviewUrlRef = useRef<string | null>(null);
  const pendingFileIdRef = useRef<string | null>(null);
  const [displayImageUrl, setDisplayImageUrl] = useState<string | null>(null);

  // Get current header image
  const { data: imageData } = trpc.company.getHeaderImage.useQuery(
    { companyId },
    { enabled: !!companyId }
  );

  useEffect(() => {
    if (imageData?.imageUrl) {
      const matchesPending =
        !pendingFileIdRef.current || imageData.fileId === pendingFileIdRef.current;
      if (matchesPending) {
        setDisplayImageUrl(imageData.imageUrl);
        if (localPreviewUrlRef.current) {
          URL.revokeObjectURL(localPreviewUrlRef.current);
          localPreviewUrlRef.current = null;
        }
        pendingFileIdRef.current = null;
      }
    } else if (!pendingFileIdRef.current && !localPreviewUrlRef.current) {
      setDisplayImageUrl(null);
    }
  }, [imageData?.imageUrl, imageData?.fileId]);

  useEffect(() => () => {
    if (localPreviewUrlRef.current) {
      URL.revokeObjectURL(localPreviewUrlRef.current);
    }
  }, []);

  // Get presigned URL for upload
  const getUploadUrlMutation = trpc.file.getUploadUrl.useMutation();
  const registerFileMutation = trpc.file.registerFile.useMutation();
  const verifyUploadMutation = trpc.file.verifyUpload.useMutation();

  // Set header image
  const setImageMutation = trpc.company.setHeaderImage.useMutation({
    onSuccess: async () => {
      await utils.company.getHeaderImage.invalidate({ companyId });
    },
    onError: (error) => {
      pendingFileIdRef.current = null;
      messageApi.error(`画像の更新に失敗しました: ${error.message}`);
    },
  });


  // Remove header image
  const removeImageMutation = trpc.company.removeHeaderImage.useMutation({
    onSuccess: () => {
      messageApi.success('ヘッダー画像を削除しました');
      utils.company.getHeaderImage.invalidate({ companyId });
    },
    onError: (error) => {
      messageApi.error(`画像の削除に失敗しました: ${error.message}`);
    },
  });

  const handleUpload = async (file: File) => {
    const previousImageUrl = displayImageUrl ?? null;
    try {
      setUploading(true);

      if (localPreviewUrlRef.current) {
        URL.revokeObjectURL(localPreviewUrlRef.current);
      }
      const previewUrl = URL.createObjectURL(file);
      localPreviewUrlRef.current = previewUrl;
      setDisplayImageUrl(previewUrl);

      // 1. Get presigned URL from backend
      const presignedData = await getUploadUrlMutation.mutateAsync({
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        category: 'header-image',
      });

      // 2. Upload file to S3 using presigned URL
      const formData = new FormData();
      Object.entries(presignedData.fields).forEach(([key, value]) => {
        formData.append(key, value as string);
      });
      formData.append('file', file);

      try {
        await fetch(presignedData.url, {
          method: 'POST',
          body: formData,
          mode: 'no-cors',
        });
      } catch {
        throw new Error('S3 upload failed. Please check your network connection and try again.');
      }

      // 3. Verify upload result from the server
      try {
        await verifyUploadMutation.mutateAsync({ key: presignedData.key });
      } catch {
        throw new Error('S3 upload verification failed. Please confirm the bucket CORS configuration.');
      }

      // 4. Register file with backend
      const fileRecord = await registerFileMutation.mutateAsync({
        s3Key: presignedData.key,
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type,
        category: 'header-image',
        associations: [{
          entityType: 'company',
          entityId: companyId,
          associationType: 'header-image',
        }],
      });

      pendingFileIdRef.current = fileRecord.id;

      // 5. Set uploaded file as header image
      await setImageMutation.mutateAsync({
        companyId,
        fileId: fileRecord.id,
      });

      let serverImageApplied = false;
      try {
        const latestImage = await utils.company.getHeaderImage.fetch({ companyId });
        if (latestImage && latestImage.fileId === fileRecord.id) {
          setDisplayImageUrl(latestImage.imageUrl);
          await utils.company.getHeaderImage.setData({ companyId }, latestImage);
          pendingFileIdRef.current = null;
          serverImageApplied = true;
        }
      } catch {
        // Ignore refresh errors
      } finally {
        if (serverImageApplied && localPreviewUrlRef.current) {
          URL.revokeObjectURL(localPreviewUrlRef.current);
          localPreviewUrlRef.current = null;
        }
      }

      messageApi.success('画像をアップロードしました');
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : '予期しないエラーが発生しました。';
      if (localPreviewUrlRef.current) {
        URL.revokeObjectURL(localPreviewUrlRef.current);
        localPreviewUrlRef.current = null;
      }
      pendingFileIdRef.current = null;
      setDisplayImageUrl(previousImageUrl);
      messageApi.error(`アップロードに失敗しました: ${errorMessage}`);
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    Modal.confirm({
      title: 'ヘッダー画像を削除',
      content:
        '本当にヘッダー画像を削除しますか？削除するとデフォルト画像が表示されるようになります。',
      okText: '削除',
      okType: 'danger',
      cancelText: 'キャンセル',
      onOk: async () => {
        await removeImageMutation.mutateAsync({ companyId });
      },
    });
  };

  const hasCustomImage = imageData?.fileId !== null;

  return (
    <>
      <div>
        <Alert
          message="ヘッダー画像について"
          description="ダッシュボードのヘッダー画像をアップロードできます。推奨サイズ: 横1000px × 縦400px程度の横長画像です。"
          type="info"
          showIcon
          className="mb-4"
        />

        <div className="mb-6">
          <div className="text-base font-semibold mb-3">現在の画像</div>
          {displayImageUrl && (
            <div className="relative">
              <div
                style={{
                  border: '2px dashed #d9d9d9',
                  borderRadius: '8px',
                  padding: '16px',
                  textAlign: 'center',
                  backgroundColor: '#fafafa',
                }}
              >
                <Image
                  src={displayImageUrl}
                  alt="Header Image"
                  style={{ maxWidth: '100%', maxHeight: '400px', objectFit: 'contain' }}
                  preview={false}
                />
              </div>
              <div
                style={{ marginTop: '12px', display: 'flex', gap: '8px', justifyContent: 'center' }}
              >
                <Button icon={<EyeOutlined />} onClick={() => setPreviewOpen(true)}>
                  拡大表示
                </Button>
              </div>
            </div>
          )}
          {!displayImageUrl && (
            <div
              style={{
                border: '2px dashed #d9d9d9',
                borderRadius: '8px',
                padding: '40px',
                textAlign: 'center',
                backgroundColor: '#fafafa',
              }}
            >
              <p style={{ color: '#999', fontSize: '14px' }}>デフォルト画像が表示されます</p>
            </div>
          )}
        </div>

        {isManager ? (
          <div>
            <Dragger
              accept="image/*"
              showUploadList={false}
              beforeUpload={(file) => {
                // Validate file size (max 10MB)
                if (file.size > 10 * 1024 * 1024) {
                  messageApi.error('ファイルサイズは10MB以下にしてください');
                  return false;
                }

                // Validate file type
                if (!file.type.startsWith('image/')) {
                  messageApi.error('画像ファイルを選択してください');
                  return false;
                }

                handleUpload(file);
                return false; // Prevent auto upload
              }}
              disabled={
                uploading || setImageMutation.isPending || verifyUploadMutation.isPending
              }
              style={{ marginBottom: '16px' }}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined style={{ color: '#1890ff' }} />
              </p>
              <p className="ant-upload-text" style={{ fontSize: '16px', fontWeight: 500 }}>
                {uploading || setImageMutation.isPending
                  ? 'アップロード中...'
                  : 'クリックまたはドラッグ&ドロップで画像をアップロード'}
              </p>
              <p className="ant-upload-hint" style={{ color: '#999' }}>
                推奨サイズ: 横1000px × 縦400px / 最大10MB
              </p>
            </Dragger>

            {hasCustomImage && (
              <div style={{ textAlign: 'center' }}>
                <Button
                  danger
                  size="large"
                  icon={<DeleteOutlined />}
                  onClick={handleRemove}
                  loading={removeImageMutation.isPending}
                >
                  デフォルトに戻す
                </Button>
              </div>
            )}
          </div>
        ) : (
          <Alert
            message="この設定を変更するにはマネージャー権限が必要です"
            type="warning"
            showIcon
          />
        )}

        <Modal
          open={previewOpen}
          title="ヘッダー画像 プレビュー"
          footer={null}
          onCancel={() => setPreviewOpen(false)}
          width="90%"
          style={{ top: 20 }}
        >
          {displayImageUrl && (
            <img src={displayImageUrl} alt="Header Image Preview" style={{ width: '100%' }} />
          )}
        </Modal>
      </div>
    </>
  );
};

export default CompanyHeaderImageSettings;
